import React from 'react';
import { TrustedAdvisorDashboard as DashboardData } from '../../types/trustedAdvisor';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { 
  Users, 
  Shield, 
  FileText, 
  Unlock, 
  Phone, 
  Activity,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface TrustedAdvisorDashboardProps {
  dashboard: DashboardData;
  onRefresh: () => void;
}

const TrustedAdvisorDashboard: React.FC<TrustedAdvisorDashboardProps> = ({ 
  dashboard, 
  onRefresh 
}) => {
  const { 
    summary, 
    advisors, 
    permissions_summary, 
    pending_unlock_requests, 
    emergency_contacts, 
    recent_activity,
    unread_notifications,
    pending_invitations_count
  } = dashboard;

  // Provide fallback values if data is missing
  const safeSummary = summary || {
    total_advisors: 0,
    active_advisors: 0,
    pending_advisors: 0,
    removed_advisors: 0
  };

  const safePermissionsSummary = permissions_summary || [];
  const safePendingUnlockRequests = pending_unlock_requests || [];
  const safeEmergencyContacts = emergency_contacts || [];
  const safeRecentActivity = recent_activity || [];
  const safePendingInvitationsCount = pending_invitations_count || 0;

  const stats = [
    {
      title: 'Total Advisors',
      value: safeSummary.total_advisors,
      icon: <Users className="w-6 h-6" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Active Advisors',
      value: safeSummary.active_advisors,
      icon: <CheckCircle className="w-6 h-6" />,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Pending Invitations',
      value: safePendingInvitationsCount,
      icon: <Clock className="w-6 h-6" />,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      title: 'Documents Shared',
      value: safePermissionsSummary.reduce((sum, p) => sum + p.total_permissions, 0),
      icon: <FileText className="w-6 h-6" />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      title: 'Pending Unlock Requests',
      value: safePendingUnlockRequests.length,
      icon: <Unlock className="w-6 h-6" />,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      title: 'Emergency Contacts',
      value: safeEmergencyContacts.length,
      icon: <Phone className="w-6 h-6" />,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'removed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'advisor_added':
        return <Users className="w-4 h-4 text-blue-500" />;
      case 'advisor_activated':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'permission_granted':
        return <Shield className="w-4 h-4 text-purple-500" />;
      case 'unlock_requested':
        return <Unlock className="w-4 h-4 text-orange-500" />;
      case 'document_accessed':
        return <FileText className="w-4 h-4 text-indigo-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'advisor_added':
        return 'Trusted Advisor Added';
      case 'advisor_activated':
        return 'Trusted Advisor Activated';
      case 'advisor_removed':
        return 'Trusted Advisor Removed';
      case 'permission_granted':
        return 'Permission Granted';
      case 'permission_revoked':
        return 'Permission Revoked';
      case 'permission_updated':
        return 'Permission Updated';
      case 'document_accessed':
        return 'Document Accessed';
      case 'document_viewed':
        return 'Document Viewed';
      case 'document_edited':
        return 'Document Edited';
      case 'unlock_requested':
        return 'Unlock Requested';
      case 'unlock_approved':
        return 'Unlock Approved';
      case 'unlock_blocked':
        return 'Unlock Blocked';
      case 'unlock_completed':
        return 'Unlock Completed';
      case 'invitation_sent':
        return 'Invitation Sent';
      case 'invitation_accepted':
        return 'Invitation Accepted';
      case 'invitation_declined':
        return 'Invitation Declined';
      default:
        return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with notifications */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
          <p className="text-gray-600 mt-1">
            Manage your trusted advisors and document sharing permissions
          </p>
        </div>
        {unread_notifications > 0 && (
          <Badge variant="warning" className="flex items-center">
            <AlertTriangle className="w-4 h-4 mr-1" />
            {unread_notifications} unread notifications
          </Badge>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <Card key={index} className="p-6">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <div className={stat.color}>{stat.icon}</div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Trusted Advisors */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Trusted Advisors</h3>
            <button
              onClick={onRefresh}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              View All
            </button>
          </div>
          <div className="space-y-4">
            {(advisors || []).slice(0, 5).map((advisor) => (
              <div key={advisor.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-semibold">
                      {advisor.first_name[0]}{advisor.last_name[0]}
                    </span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">
                      {advisor.first_name} {advisor.last_name}
                    </p>
                    <p className="text-sm text-gray-500">{advisor.relationship}</p>
                  </div>
                </div>
                <Badge className={getStatusColor(advisor.status)}>
                  {advisor.status}
                </Badge>
              </div>
            ))}
            {(!advisors || advisors.length === 0) && (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No trusted advisors yet</p>
                <p className="text-sm">Add your first trusted advisor to get started</p>
              </div>
            )}
          </div>
        </Card>

        {/* Pending Unlock Requests */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Pending Unlock Requests</h3>
            {safePendingUnlockRequests.length > 0 && (
              <Badge variant="warning">{safePendingUnlockRequests.length}</Badge>
            )}
          </div>
          <div className="space-y-4">
            {safePendingUnlockRequests.slice(0, 5).map((request) => (
              <div key={request.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                <div className="flex items-center">
                  <Unlock className="w-5 h-5 text-orange-500 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {request.first_name} {request.last_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      Requested access to {request.document_type}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">
                    {formatDistanceToNow(new Date(request.requested_at), { addSuffix: true })}
                  </p>
                  <Badge variant="warning" className="mt-1">Pending</Badge>
                </div>
              </div>
            ))}
            {safePendingUnlockRequests.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Unlock className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No pending unlock requests</p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
          <button
            onClick={onRefresh}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            View All
          </button>
        </div>
        <div className="space-y-4">
          {safeRecentActivity.slice(0, 10).map((log) => (
            <div key={log.id} className="flex items-center p-3 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0 mr-3">
                {getActionIcon(log.action)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  {getActionLabel(log.action)}
                </p>
                {log.document_type && (
                  <p className="text-sm text-gray-500">
                    Document: {log.document_type}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">
                  {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                </p>
              </div>
            </div>
          ))}
          {safeRecentActivity.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Activity className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No recent activity</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default TrustedAdvisorDashboard; 